import React, { useCallback } from "react";
import type { UseFormRegister } from "react-hook-form";

import Skeleton from "components/skeleton";

type HookFormRadioButtonProps = {
	register: UseFormRegister<any>;
	className?: string;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	blurLoading?: boolean;
	blurWidth?: string;
	blurHeight?: string;
	blurMarginLeft?: string;
	name: string;
	id?: string;
	label?: string;
	disabled?: boolean;
	tabIndex?: number;
	defaultChecked?: boolean;
	defaultCheckedByValue?: string;
	value?: string;
} & React.HTMLProps<HTMLInputElement>;

const HookFormRadioButton: React.FC<HookFormRadioButtonProps> = ({
	id,
	className,
	label,
	disabled,
	tabIndex,
	blurLoading,
	blurWidth = "50px",
	blurHeight = "25px",
	blurMarginLeft,
	defaultChecked = false,
	defaultCheckedByValue,
	register,
	name,
	value,
	onChange,
	...props
}) => {
	const {
		onChange: formOnChange,
		onBlur,
		name: nameForm,
		...rest
	} = register(name);

	const RadioButtonWithLoader = useCallback(
		({ button }) =>
			blurLoading ? (
				<Skeleton
					variant="text"
					width={blurWidth}
					height={blurHeight}
					marginLeft={blurMarginLeft}
				/>
			) : (
				button
			),
		[blurLoading, blurWidth, blurHeight, blurMarginLeft],
	);

	const button = (
		<div className={className}>
			<input
				{...rest}
				onBlur={onBlur}
				onChange={event => {
					formOnChange?.(event);
					onChange?.(event);
				}}
				name={nameForm}
				disabled={disabled}
				id={id}
				type="radio"
				tabIndex={tabIndex}
				value={value}
				defaultChecked={
					defaultCheckedByValue && defaultCheckedByValue === value
						? true
						: defaultChecked
				}
				{...props}
			/>
			<label htmlFor={id} className={disabled ? "disabled" : ""}>
				{label}
			</label>
		</div>
	);

	return <RadioButtonWithLoader button={button} />;
};

export default HookFormRadioButton;
